import React, { useEffect, useRef, useState } from 'react'
import { ApolloError } from '@apollo/client'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import { ChatLink } from '../../api/patientConversation'
import { usePatientDraft } from '../../api/patientDraft'
import { advancedColors } from '../../theme'
import { useDisabledInPreview } from '../panelPreviewContext'
import { AttachmentButton, AttachmentPreview, useAttachmentUpload } from './attachments/AttachmentPicker'
import LinkedContentCard from './LinkedContentCard'
import { PendingComment } from './types'
import { chatComposerInputSx } from './chatComposerStyles'
import { useChatInputHeight } from './ChatComposerLayout'

type Editing = { messageID: string; text: string }

// `createChatMessage` names the files it rejected; they are already deleted server-side.
const rejectedFileIDsOf = (failure: unknown): string[] => {
  const ids = failure instanceof ApolloError ? failure.graphQLErrors[0]?.extensions?.rejectedFileIDs : undefined
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []
}

type Props = {
  uid: string
  careTeamChatOpen?: boolean
  attachmentsEnabled?: boolean
  inAppChatEnabled: boolean
  sending?: boolean
  editing: Editing | null
  onCancelEdit: () => void
  onSendChat: (text: string, linksTo?: ChatLink | null, fileIDs?: string[]) => Promise<unknown>
  sendBlockedNotice?: string | null
  onEdit: (messageID: string, text: string) => Promise<unknown>
  pendingComment?: PendingComment | null
  onClearPendingComment?: () => void
}

const bannerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  px: 1,
  pt: 1,
  borderTop: `1px solid ${advancedColors.line}`,
}

const PatientChatComposer = ({
  uid,
  careTeamChatOpen = false,
  attachmentsEnabled = true,
  inAppChatEnabled,
  sending,
  editing,
  onCancelEdit,
  onSendChat,
  sendBlockedNotice,
  onEdit,
  pendingComment,
  onClearPendingComment,
}: Props) => {
  const disabled = useDisabledInPreview(sending || !inAppChatEnabled || careTeamChatOpen)
  const [draft, setDraft] = usePatientDraft(uid)
  const [editText, setEditText] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const attachments = useAttachmentUpload(uid, disabled)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const inputHeight = useChatInputHeight('participant', inputRef)

  useEffect(() => {
    setEditText(editing?.text ?? '')
  }, [editing])

  useEffect(() => {
    if (!careTeamChatOpen && (pendingComment || editing)) {
      inputRef.current?.focus()
    }
  }, [pendingComment, editing, careTeamChatOpen])

  const value = editing ? editText : draft
  const setValue = editing ? setEditText : setDraft
  // Mid-upload files would miss this message and land in the next draft.
  const busy = disabled || attachments.uploading
  const pendingFiles = attachmentsEnabled ? attachments.pending : []
  const canSubmit = !busy && (Boolean(value.trim()) || pendingFiles.length > 0)

  const submit = async () => {
    const text = value.trim()
    if (busy) {
      return
    }
    if (editing) {
      if (!text) {
        setNotice('Message cannot be empty')
        return
      }
      await onEdit(editing.messageID, text)
      onCancelEdit()
      return
    }
    if (!text && pendingFiles.length === 0) {
      return
    }
    if (sendBlockedNotice) {
      setNotice(sendBlockedNotice)
      return
    }
    setNotice(null)
    try {
      await onSendChat(
        text,
        pendingComment ? { type: pendingComment.type, id: pendingComment.id } : null,
        pendingFiles.map((file) => file.fileID)
      )
    } catch (failure) {
      rejectedFileIDsOf(failure).forEach(attachments.remove)
      return
    }
    onClearPendingComment?.()
    attachments.clear()
    setDraft('')
  }

  const run = () => {
    submit().catch(() => undefined)
  }

  return (
    <>
      {careTeamChatOpen && (
        <Box sx={{ borderTop: `1px solid ${advancedColors.line}`, p: 1.5, backgroundColor: advancedColors.canvas }}>
          <Typography role="status" variant="body2" sx={{ color: advancedColors.inkMuted }}>
            Participant chat is disabled while the care team chat is open.
          </Typography>
        </Box>
      )}
      <Box sx={{ display: careTeamChatOpen ? 'none' : 'contents' }}>
        {!inAppChatEnabled && (
          <Typography variant="caption" sx={{ px: 1, pt: 1, color: advancedColors.inkMuted }}>
            In-app chat is off for this patient.
          </Typography>
        )}

        {editing && (
          <Box sx={bannerSx}>
            <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
              Editing message (available for 5 minutes)
            </Typography>
            <Button size="small" onClick={onCancelEdit} aria-label="Cancel message editing">
              Cancel
            </Button>
          </Box>
        )}

        {notice && (
          <Alert severity="error" sx={{ mx: 1, mt: 1 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {draft.trim() && !editing && (
          <Typography variant="caption" sx={{ color: advancedColors.inkFaint, px: 1.5, py: 0.5 }}>
            Draft saved
          </Typography>
        )}

        <Box sx={{ borderTop: editing ? 'none' : `1px solid ${advancedColors.line}` }}>
          {pendingComment && !editing && (
            <Box sx={{ '& .MuiContainer-root': { pt: 1.5 } }}>
              <LinkedContentCard link={pendingComment} onRemove={onClearPendingComment} />
            </Box>
          )}

          {!editing && attachmentsEnabled && (
            <AttachmentPreview
              pending={attachments.pending}
              onRemove={attachments.remove}
              uploading={attachments.uploading}
              error={attachments.error}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', p: 1.5, '& > button': { mb: '2px' } }}>
            {!editing && attachmentsEnabled && (
              <AttachmentButton
                disabled={disabled}
                onSelect={(files) => {
                  attachments.addFiles(files).catch(() => undefined)
                }}
              />
            )}
            <TextField
              fullWidth
              size="small"
              sx={chatComposerInputSx(advancedColors.green, inputHeight)}
              multiline
              maxRows={6}
              inputProps={{ 'aria-label': 'Message the patient' }}
              placeholder="Message the patient…"
              inputRef={inputRef}
              disabled={disabled}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault()
                  run()
                }
              }}
            />
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={run}
              sx={{ height: 40, borderRadius: '8px', flexShrink: 0 }}
            >
              {editing ? 'Save' : 'Send'}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default PatientChatComposer
